import React from "react";
import { Box, Typography, Tooltip } from "@mui/material";
import { TireChangeDto } from "../../../api/maintenanceApi";

const TIRE_POSITIONS = [
  { id: "RF", label: "Right Front", x: 30, y: 20 },
  { id: "LF", label: "Left Front", x: 30, y: 100 },
  { id: "RRO1", label: "Right Rear Outer 1", x: 120, y: 5 },
  { id: "RRI1", label: "Right Rear Inner 1", x: 120, y: 35 },
  { id: "LRI1", label: "Left Rear Inner 1", x: 120, y: 85 },
  { id: "LRO1", label: "Left Rear Outer 1", x: 120, y: 115 },
  { id: "RRO2", label: "Right Rear Outer 2", x: 170, y: 5 },
  { id: "RRI2", label: "Right Rear Inner 2", x: 170, y: 35 },
  { id: "LRI2", label: "Left Rear Inner 2", x: 170, y: 85 },
  { id: "LRO2", label: "Left Rear Outer 2", x: 170, y: 115 },
];

const STEER_POSITIONS = new Set(["LF", "RF"]);

const formatDate = (value: string | null | undefined): string => {
  if (!value) return "";
  try {
    return new Date(value).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return "";
  }
};

type TireStatus = "good" | "warning" | "critical" | "default";

const getTireStatus = (tire: TireChangeDto): TireStatus => {
  const isSteer = STEER_POSITIONS.has(tire.position);

  const padStatus = getPadStatus(tire.brakePadMm);
  const treadStatus = getTreadStatus(tire.treadDepth32nds, isSteer);

  if (padStatus === "critical" || treadStatus === "critical") return "critical";
  if (padStatus === "warning" || treadStatus === "warning") return "warning";
  return "good";
};

const getTreadStatus = (
  depth: number | null,
  isSteer: boolean
): TireStatus => {
  if (depth == null) return "default";
  const min = isSteer ? 4 : 2;
  if (depth <= min) return "critical";
  if (depth <= min + 2) return "warning";
  return "good";
};

const getPadStatus = (padMm: number | null): TireStatus => {
  if (padMm == null) return "default";
  if (padMm <= 3) return "critical";
  if (padMm <= 6) return "warning";
  return "good";
};

const STATUS_COLORS: Record<TireStatus, { fill: string; stroke: string; text: string }> = {
  good: { fill: "#4caf50", stroke: "#388e3c", text: "#fff" },
  warning: { fill: "#ff9800", stroke: "#e65100", text: "#fff" },
  critical: { fill: "#f44336", stroke: "#c62828", text: "#fff" },
  default: { fill: "#e0e0e0", stroke: "#9e9e9e", text: "#616161" },
};

interface TireDiagramProps {
  tires: TireChangeDto[];
  onToggle?: (positionId: string) => void;
  readOnly?: boolean;
}

const TireDiagram: React.FC<TireDiagramProps> = ({
  tires,
  onToggle,
  readOnly = false,
}) => {
  const tireMap = new Map(tires.map((t) => [t.position, t]));

  const handleTireClick = (id: string) => {
    if (readOnly || !onToggle) return;
    onToggle(id);
  };

  const buildTooltip = (tire: (typeof TIRE_POSITIONS)[0]) => {
    const data = tireMap.get(tire.id);
    const lines: string[] = [`${tire.label} (${tire.id})`];
    if (data) {
      if (data.date) lines.push(`Changed: ${formatDate(data.date)}`);
      if (data.treadDepth32nds != null)
        lines.push(`Tread: ${data.treadDepth32nds}/32"`);
      if (data.brakePadMm != null) lines.push(`Brake pad: ${data.brakePadMm}mm`);
      if (data.note) lines.push(`Note: ${data.note}`);
    }
    return lines.join("\n");
  };

  return (
    <Box sx={{ display: "inline-block" }}>
      <svg width="220" height="130" viewBox="0 0 220 130">
        <rect
          x="15" y="30" width="45" height="60" rx="8"
          fill="none" stroke="#9e9e9e" strokeWidth="1.5"
        />
        <rect
          x="60" y="40" width="100" height="40" rx="3"
          fill="none" stroke="#9e9e9e" strokeWidth="1.5"
        />
        <line x1="30" y1="30" x2="30" y2="15" stroke="#bdbdbd" strokeWidth="1" />
        <line x1="30" y1="90" x2="30" y2="105" stroke="#bdbdbd" strokeWidth="1" />
        <line x1="120" y1="40" x2="120" y2="5" stroke="#bdbdbd" strokeWidth="1" />
        <line x1="120" y1="80" x2="120" y2="115" stroke="#bdbdbd" strokeWidth="1" />
        <line x1="170" y1="40" x2="170" y2="5" stroke="#bdbdbd" strokeWidth="1" />
        <line x1="170" y1="80" x2="170" y2="115" stroke="#bdbdbd" strokeWidth="1" />

        {TIRE_POSITIONS.map((tire) => {
          const data = tireMap.get(tire.id);
          const isChanged = !!data;
          const status = data ? getTireStatus(data) : "default";
          const colors = STATUS_COLORS[status];

          return (
            <Tooltip
              key={tire.id}
              title={
                <span style={{ whiteSpace: "pre-line" }}>
                  {buildTooltip(tire)}
                </span>
              }
              arrow
            >
              <g
                onClick={() => handleTireClick(tire.id)}
                style={{ cursor: readOnly ? "default" : "pointer" }}
              >
                <rect
                  x={tire.x - 8} y={tire.y - 5} width="16" height="10" rx="3"
                  fill={isChanged ? colors.fill : STATUS_COLORS.default.fill}
                  stroke={isChanged ? colors.stroke : STATUS_COLORS.default.stroke}
                  strokeWidth="1"
                />
                <text
                  x={tire.x} y={tire.y + 3}
                  textAnchor="middle" fontSize="5"
                  fill={isChanged ? colors.text : STATUS_COLORS.default.text}
                  fontWeight="bold"
                >
                  {tire.id}
                </text>
              </g>
            </Tooltip>
          );
        })}
      </svg>
      <Box sx={{ display: "flex", gap: 1.5, mt: 0.5, justifyContent: "center", flexWrap: "wrap" }}>
        {[
          { color: STATUS_COLORS.good.fill, label: "Good" },
          { color: STATUS_COLORS.warning.fill, label: "Caution" },
          { color: STATUS_COLORS.critical.fill, label: "Replace" },
          { color: STATUS_COLORS.default.fill, label: "Not Set" },
        ].map((item) => (
          <Box key={item.label} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: "2px", bgcolor: item.color }} />
            <Typography variant="caption" color="text.secondary">
              {item.label}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default TireDiagram;
